import { chatReadStateService } from '@/entities/ChatDialog/api/chatReadStateService';
import type { ChatDialogPageWithTotal, ChatRealtimeSnapshotWithTotal } from '../model/types';
import { getChatApi } from '@/core/api/generatedApiClient';
import { rethrowHandledApiError } from '@/core/api/rethrowHandledApiError';
import type {
    ChatApiGetChatDialogsRequest,
    ChatApiRetryChatDialogMessageRequest,
    ChatApiSendChatDialogMessageRequest,
    ChatDialogMessageResponse,
    ChatApiGetChatDialogMessagesRequest,
    ChatDialogCountersResponse,
    ChatDialogMessagePageResponse,
    ChatRealtimeResponse,
    ChatApiGetChatDialogSnapshotRequest,
} from '@/core/generated/api';

/** Предоставляет операции переписки с пользователями */
export const chatDialogService = {
    /** Получает доступ к обновлениям переписки */
    async getRealtime(): Promise<ChatRealtimeResponse> {
        try {
            const response = await getChatApi().getChatDialogRealtime();

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Получает согласованное состояние переписки */
    async getSnapshot(
        request: ChatApiGetChatDialogSnapshotRequest
    ): Promise<ChatRealtimeSnapshotWithTotal> {
        try {
            const response = await getChatApi().getChatDialogSnapshot(request);

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Загружает страницу диалогов */
    async getDialogs(request: ChatApiGetChatDialogsRequest): Promise<ChatDialogPageWithTotal> {
        try {
            const response = await getChatApi().getChatDialogs(request);

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Загружает страницу истории диалога */
    async getMessages(
        request: ChatApiGetChatDialogMessagesRequest
    ): Promise<ChatDialogMessagePageResponse> {
        try {
            const response = await getChatApi().getChatDialogMessages(request);

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Загружает счётчики переписки */
    async getCounters(): Promise<ChatDialogCountersResponse> {
        try {
            const response = await getChatApi().getChatDialogCounters();

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Сохраняет ответ пользователю */
    async sendMessage(
        request: ChatApiSendChatDialogMessageRequest
    ): Promise<ChatDialogMessageResponse> {
        try {
            const response = await getChatApi().sendChatDialogMessage(request);

            return response.data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Возвращает существующее сообщение в очередь после указанной попытки */
    async retryMessage(
        request: ChatApiRetryChatDialogMessageRequest
    ): Promise<ChatDialogMessageResponse> {
        try {
            return (await getChatApi().retryChatDialogMessage(request)).data;
        } catch (error: unknown) {
            return rethrowHandledApiError(error);
        }
    },

    /** Подтверждает прочтение до показанного сообщения */
    async markRead(dialogId: string, throughMessageId: string): Promise<void> {
        await chatReadStateService.confirm(false, dialogId, throughMessageId);
    },
};
